import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { 
  FiSearch, FiFilter, FiChevronRight, FiArrowLeft, 
  FiTruck, FiMapPin, FiStar, FiCheckCircle, 
  FiDollarSign, FiMessageSquare, FiCalendar, 
  FiAlertCircle, FiTag, FiLoader, FiUser, FiActivity
} from 'react-icons/fi';
import { differenceInDays } from 'date-fns';
import supabase from '../supabase/supabase';

export const Salvagers = () => {
  const [selectedSalvager, setSelectedSalvager] = useState(null);
  const [salvagers, setSalvagers] = useState([]);
  const [recentPayouts, setRecentPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search States
  const [salvagerSearchQuery, setSalvagerSearchQuery] = useState('');
  const [claimSearchQuery, setClaimSearchQuery] = useState('');

  useEffect(() => {
    fetchSalvagersAndClaims();
  }, []);

  const fetchSalvagersAndClaims = async () => {
    setLoading(true);
    try {
      // Fetch all Salvager profiles
      const { data: profiles, error: profileError } = await supabase
        .from('profiles_1783455000000')
        .select('*')
        .eq('role', 'SALVAGER');
      if (profileError) throw profileError;

      // Fetch all claims assigned to salvagers
      const { data: claims, error: claimsError } = await supabase
        .from('claims_1783447171616')
        .select('*')
        .not('assigned_salvager_id', 'is', null);
      if (claimsError) throw claimsError;

      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      // Map the DB data to our UI structure
      const enrichedSalvagers = (profiles || []).map(profile => {
        let totalPayouts = 0;
        let payoutsThisMonth = 0;
        let payoutsThisYear = 0;
        let paidCount = 0;
        let issueCount = 0;
        let outstandingCount = 0;
        let pendingPayoutTotal = 0;

        const assignedClaims = (claims || [])
          .filter(c => c.assigned_salvager_id === profile.id)
          .map(c => {
            const val = parseFloat(c.offer_price || c.estimated_value || 0);
            const isPaid = c.payment_status === 'Paid';
            const claimDate = new Date(c.created_at);

            // Calculate Metrics
            if (isPaid) {
              totalPayouts += val;
              paidCount++;
              if (claimDate.getFullYear() === currentYear) {
                payoutsThisYear += val;
                if (claimDate.getMonth() === currentMonth) {
                  payoutsThisMonth += val;
                }
              }
            } else {
              pendingPayoutTotal += val;
            }

            if (c.status === 'Issue') {
              issueCount++;
            }

            if (!isPaid && c.status !== 'Completed' && c.status !== 'Declined') {
              outstandingCount++;
            }

            return {
              id: c.id,
              number: c.claim_number,
              item: c.salvage_item || `${c.make} ${c.model}`,
              status: c.status,
              note: c.ber_reason || 'No specific notes',
              paymentStatus: c.payment_status || 'Unpaid',
              value: val,
              formattedValue: `R ${val.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
              allocatedAt: c.created_at
            };
          });

        const avgPayout = paidCount > 0 ? (totalPayouts / paidCount) : 0;

        return {
          id: profile.id,
          name: profile.company_name || profile.full_name || 'Unnamed Company',
          contact: profile.full_name || 'No contact provided',
          location: profile.company_details || 'Location not specified',
          avatar_url: profile.avatar_url,
          tags: profile.tags || [],
          rating: '5.0', // Placeholder until review system is built
          status: 'Active',
          assignedClaims,
          // Exposing these directly for the UI and AI to utilize easily
          metrics: {
            totalPayouts,
            payoutsThisMonth,
            payoutsThisYear,
            avgPayout,
            issueCount,
            outstandingCount,
            pendingPayoutTotal
          }
        };
      });

      // Sort by best performing (highest total payouts) as default
      enrichedSalvagers.sort((a, b) => b.metrics.totalPayouts - a.metrics.totalPayouts);
      
      setSalvagers(enrichedSalvagers);

      // Extract Recent Financial Activity
      const payouts = (claims || [])
        .filter(c => c.payment_status === 'Paid')
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) 
        .slice(0, 5) 
        .map(c => {
          const salvagerInfo = (profiles || []).find(p => p.id === c.assigned_salvager_id);
          return {
            id: c.id,
            claim_number: c.claim_number,
            salvagerName: salvagerInfo?.company_name || 'Unknown Salvager',
            amount: parseFloat(c.offer_price || c.estimated_value || 0),
            date: new Date(c.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
          };
        });
      
      setRecentPayouts(payouts);

    } catch (err) {
      console.error('Error fetching salvagers:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateDays = (dateString) => {
    return differenceInDays(new Date(), new Date(dateString));
  };

  const handleSelectSalvager = (salvager) => {
    setSelectedSalvager(salvager);
    setClaimSearchQuery(''); // Reset claim search when switching salvagers
  };

  if (selectedSalvager) {
    // Live filter for the selected salvager's claims
    const filteredAssignedClaims = selectedSalvager.assignedClaims.filter(c => {
      if (!claimSearchQuery) return true;
      const q = claimSearchQuery.toLowerCase();
      return (
        (c.number && c.number.toLowerCase().includes(q)) ||
        (c.item && c.item.toLowerCase().includes(q)) ||
        (c.note && c.note.toLowerCase().includes(q)) ||
        (c.status && c.status.toLowerCase().includes(q))
      );
    });

    return (
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Back Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => handleSelectSalvager(null)}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-500"
            >
              <SafeIcon icon={FiArrowLeft} size={24} />
            </button>
            <div className="flex items-center gap-5">
              {selectedSalvager.avatar_url ? (
                <img src={selectedSalvager.avatar_url} alt={selectedSalvager.name} className="w-16 h-16 rounded-full object-cover shadow-sm border border-gray-100" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-brand/10 text-brand flex items-center justify-center shadow-sm">
                  <SafeIcon icon={FiTruck} size={28} />
                </div>
              )}
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-gray-900">{selectedSalvager.name}</h1>
                  <Badge status={selectedSalvager.status}>{selectedSalvager.status}</Badge>
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                  <span className="flex items-center gap-1.5"><SafeIcon icon={FiUser} className="text-brand" /> {selectedSalvager.contact}</span>
                  <span className="flex items-center gap-1.5"><SafeIcon icon={FiMapPin} className="text-brand" /> {selectedSalvager.location.substring(0, 40)}{selectedSalvager.location.length > 40 ? '...' : ''}</span>
                  <span className="flex items-center gap-1.5"><SafeIcon icon={FiStar} className="text-yellow-400" /> {selectedSalvager.rating} Partner Rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tags Section */}
        {selectedSalvager.tags && selectedSalvager.tags.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <SafeIcon icon={FiTag} /> Collection Categories & Niches
            </h3>
            <div className="flex flex-wrap gap-2">
              {selectedSalvager.tags.map((tag, idx) => (
                <span key={idx} className="px-3 py-1.5 bg-brand/10 text-brand border border-brand/20 rounded-lg text-xs font-bold shadow-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* AI Performance Metrics Grid */}
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <SafeIcon icon={FiActivity} /> Performance Metrics
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          <Card className="flex flex-col justify-center py-5">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Total Payouts</p>
            <h3 className="text-lg font-bold text-green-600">
              R {selectedSalvager.metrics.totalPayouts.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
            </h3>
          </Card>
          <Card className="flex flex-col justify-center py-5">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">This Year</p>
            <h3 className="text-lg font-bold text-gray-900">
              R {selectedSalvager.metrics.payoutsThisYear.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
            </h3>
          </Card>
          <Card className="flex flex-col justify-center py-5">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">This Month</p>
            <h3 className="text-lg font-bold text-gray-900">
              R {selectedSalvager.metrics.payoutsThisMonth.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
            </h3>
          </Card>
          <Card className="flex flex-col justify-center py-5">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Avg / Item</p>
            <h3 className="text-lg font-bold text-gray-900">
              R {selectedSalvager.metrics.avgPayout.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
            </h3>
          </Card>
          <Card className="flex flex-col justify-center py-5 border-l-4 border-blue-500">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Outstanding</p>
            <h3 className="text-lg font-bold text-gray-900">
              {selectedSalvager.metrics.outstandingCount} Claims
            </h3>
          </Card>
          <Card className="flex flex-col justify-center py-5 border-l-4 border-orange-500">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Issues</p>
            <h3 className="text-lg font-bold text-orange-600">
              {selectedSalvager.metrics.issueCount} Claims
            </h3>
          </Card>
        </div>

        {/* Detailed Claims Table */}
        <Card className="overflow-hidden !p-0">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/30">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              Assigned Claims Inventory 
              <span className="bg-gray-200 text-gray-600 text-[10px] px-2 py-0.5 rounded-full font-bold">
                {filteredAssignedClaims.length}
              </span>
            </h3>
            <div className="flex gap-2">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  value={claimSearchQuery}
                  onChange={(e) => setClaimSearchQuery(e.target.value)}
                  placeholder="Filter claims..." 
                  className="pl-9 pr-4 py-1.5 bg-white border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-brand/20 outline-none w-48"
                />
              </div>
              <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium hover:bg-gray-50">
                <SafeIcon icon={FiFilter} /> Status
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 text-[10px] uppercase text-gray-400 tracking-widest bg-gray-50/50">
                  <th className="px-6 py-4 font-bold">Claim Reference</th>
                  <th className="px-6 py-4 font-bold">Line Item</th>
                  <th className="px-6 py-4 font-bold">Status</th>
                  <th className="px-6 py-4 font-bold">Latest Internal Note</th>
                  <th className="px-6 py-4 font-bold text-center">Days Allocated</th>
                  <th className="px-6 py-4 font-bold">Value</th>
                  <th className="px-6 py-4 font-bold text-right">Payment</th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-gray-50">
                {filteredAssignedClaims.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3 text-gray-400">
                        <SafeIcon icon={FiTruck} size={48} className="opacity-20" />
                        <p className="italic text-sm">
                          {claimSearchQuery ? 'No claims match your filter.' : 'No claims currently assigned to this salvager.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAssignedClaims.map((claim) => {
                    const days = calculateDays(claim.allocatedAt);
                    return (
                      <tr key={claim.id} className="hover:bg-gray-50/50 transition-colors group">
                        <td className="px-6 py-5">
                          <span className="font-bold text-gray-900 group-hover:text-brand transition-colors">{claim.number}</span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-700">{claim.item}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <Badge status={claim.status === 'Completed' ? 'Completed' : claim.status === 'Issue' ? 'Open' : 'Pending'}>
                            {claim.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-5 max-w-xs">
                          <div className="flex items-start gap-2 text-gray-500 text-xs leading-relaxed">
                            <SafeIcon icon={FiMessageSquare} className="mt-1 text-brand/40 flex-shrink-0" />
                            <span className="italic">{claim.note}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${days > 7 ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                            <SafeIcon icon={FiCalendar} /> {days} Days
                          </div>
                        </td>
                        <td className="px-6 py-5 font-bold text-gray-900">{claim.formattedValue}</td>
                        <td className="px-6 py-5 text-right">
                          <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                            claim.paymentStatus === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600 border border-red-100'
                          }`}>
                            {claim.paymentStatus}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Actions */}
          <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
            <button className="px-4 py-2 text-xs font-bold text-gray-500 hover:text-gray-700 transition-colors">
              Download Inventory Report
            </button>
            <button className="px-6 py-2 bg-brand text-white rounded-xl text-xs font-bold hover:bg-brand-light transition-all shadow-sm">
              Settle All Completed
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // Live filter for the main Salvagers grid
  const filteredSalvagers = salvagers.filter(s => {
    if (!salvagerSearchQuery) return true;
    const q = salvagerSearchQuery.toLowerCase();
    return (
      (s.name && s.name.toLowerCase().includes(q)) ||
      (s.location && s.location.toLowerCase().includes(q)) ||
      (s.tags && s.tags.some(tag => tag.toLowerCase().includes(q)))
    );
  });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Salvager Partners</h1>
          <p className="text-gray-500">Manage and monitor your registered salvage network.</p>
        </div>
        <button className="px-4 py-2 bg-brand text-white rounded-xl hover:bg-brand-light font-medium text-sm transition-colors shadow-sm">
          + Invite Partner
        </button>
      </div>

      {/* Main Salvagers Search Bar */}
      {!loading && salvagers.length > 0 && (
        <div className="max-w-md relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            value={salvagerSearchQuery}
            onChange={(e) => setSalvagerSearchQuery(e.target.value)}
            placeholder="Search salvagers by name, location, or tags..." 
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 text-sm shadow-sm"
          />
        </div>
      )}

      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center text-gray-400">
          <SafeIcon icon={FiLoader} className="animate-spin text-4xl mb-4 text-brand" />
          <p>Loading registered salvagers...</p>
        </div>
      ) : salvagers.length === 0 ? (
        <div className="py-20 flex flex-col items-center justify-center text-gray-400 bg-white rounded-3xl border border-gray-100 shadow-sm">
          <SafeIcon icon={FiTruck} className="text-6xl mb-4 text-gray-200" />
          <p className="text-lg font-medium text-gray-600">No Salvagers Found</p>
          <p className="text-sm mt-2">Salvagers who register will appear here automatically.</p>
        </div>
      ) : filteredSalvagers.length === 0 ? (
        <div className="py-20 flex flex-col items-center justify-center text-gray-400 bg-white rounded-3xl border border-gray-100 shadow-sm">
          <SafeIcon icon={FiSearch} className="text-6xl mb-4 text-gray-200" />
          <p className="text-lg font-medium text-gray-600">No Match Found</p>
          <p className="text-sm mt-2">No salvagers match your search criteria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredSalvagers.map((salvager) => (
            <Card 
              key={salvager.id} 
              className="group hover:scale-[1.02] hover:border-brand/30 border border-transparent transition-all cursor-pointer flex flex-col h-full shadow-sm"
              onClick={() => handleSelectSalvager(salvager)}
            >
              <div className="flex justify-between items-start mb-4">
                {salvager.avatar_url ? (
                  <img src={salvager.avatar_url} alt={salvager.name} className="w-12 h-12 rounded-2xl object-cover shadow-sm" />
                ) : (
                  <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-brand group-hover:bg-brand-accent transition-colors">
                    <SafeIcon icon={FiTruck} size={24} />
                  </div>
                )}
                <div className="flex items-center gap-1 text-yellow-500 font-bold text-sm">
                  <SafeIcon icon={FiStar} /> {salvager.rating}
                </div>
              </div>
              
              <h3 className="font-bold text-gray-900 text-lg mb-1 group-hover:text-brand transition-colors line-clamp-1">
                {salvager.name}
              </h3>
              <p className="text-xs text-gray-500 flex items-center gap-1 mb-3 line-clamp-1">
                <SafeIcon icon={FiMapPin} className="flex-shrink-0" /> {salvager.location}
              </p>

              {/* Tag Preview */}
              <div className="flex flex-wrap gap-1 mb-4 h-[24px] overflow-hidden">
                {salvager.tags && salvager.tags.length > 0 ? (
                  salvager.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-brand/10 text-brand rounded text-[10px] font-bold whitespace-nowrap">
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="text-[10px] text-gray-400 italic">No specific tags</span>
                )}
                {salvager.tags && salvager.tags.length > 3 && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-bold">
                    +{salvager.tags.length - 3}
                  </span>
                )}
              </div>

              {/* Real Metric Summary */}
              <div className="space-y-2 mb-6 flex-1 bg-gray-50 p-3 rounded-xl border border-gray-100/50">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-medium">Outstanding:</span>
                  <span className="font-bold text-gray-900">{salvager.metrics.outstandingCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-medium">Issues:</span>
                  <span className="font-bold text-orange-600">{salvager.metrics.issueCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-200 mt-2">
                  <span className="text-gray-500 font-medium pb-1">Total Paid:</span>
                  <span className="font-bold text-green-600">
                    R {salvager.metrics.totalPayouts.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-50 flex justify-between items-center">
                <Badge status={salvager.status}>{salvager.status}</Badge>
                <div className="text-brand flex items-center gap-1 text-xs font-bold">
                  Metrics <SafeIcon icon={FiChevronRight} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Global Payouts Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <h3 className="font-bold text-gray-800 mb-6 flex items-center gap-2">
            <SafeIcon icon={FiDollarSign} className="text-brand" /> Recent Financial Activity
          </h3>
          <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
            {recentPayouts.length === 0 ? (
               <div className="text-center text-gray-400 py-10 italic text-sm">
                 No recent payouts found in the system.
               </div>
            ) : (
              recentPayouts.map((payout) => (
                <div key={payout.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-green-600 shadow-sm">
                      <SafeIcon icon={FiCheckCircle} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">Payment Processed</p>
                      <p className="text-xs text-gray-500">{payout.salvagerName} • {payout.claim_number}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">
                      R {payout.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">{payout.date}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="flex flex-col justify-center items-center text-center p-8 bg-brand-dark text-white relative overflow-hidden">
          <div className="relative z-10">
            <div className="w-16 h-16 rounded-3xl bg-white/10 flex items-center justify-center mb-6 mx-auto">
              <SafeIcon icon={FiTruck} size={32} />
            </div>
            <h3 className="text-xl font-bold mb-2">Partner Network</h3>
            <p className="text-sm text-brand-accent/70 mb-8">
              Grow your salvage network by inviting trusted recyclers and buyers.
            </p>
            <button className="w-full py-3 bg-brand text-white rounded-xl font-bold text-sm hover:bg-brand-light transition-all">
              Copy Invite Link
            </button>
          </div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-brand rounded-full opacity-20 blur-3xl"></div>
        </Card>
      </div>
    </div>
  );
};